import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, Heart, Target, Users, Zap, 
  ChevronRight, Star, Check, ArrowRight,
  Lightbulb, TrendingUp, Award, Sparkles
} from 'lucide-react';
import { MobileChoiceSelector, MobileComparisonSelector } from './MobileInteractionLibrary';
import { AIOutputComparison } from './AIOutputComparison';

interface MayaScenario {
  id: string;
  title: string;
  description: string;
  challenge: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  category: 'email' | 'social' | 'presentation' | 'meeting';
}

interface StoryStage {
  id: string;
  title: string;
  narrative: string;
  choices?: Array<{
    id: string;
    text: string;
    impact: 'positive' | 'negative' | 'neutral';
    consequence: string;
  }>;
  lesson?: string;
  tips?: string[];
}

const MAYA_SCENARIOS: MayaScenario[] = [
  {
    id: 'board-email',
    title: 'Board Communication Crisis',
    description: 'Help Maya craft a crucial email to the board about program challenges',
    challenge: 'Maya needs to inform the board about delays in the summer program launch while maintaining confidence and providing solutions.',
    difficulty: 'intermediate',
    category: 'email'
  },
  {
    id: 'team-motivation',
    title: 'Team Morale Challenge',
    description: 'Maya must motivate her team after a setback',
    challenge: 'The team is demoralized after losing a major client. Maya needs to re-energize them and refocus on opportunities.',
    difficulty: 'advanced',
    category: 'meeting'
  },
  {
    id: 'client-recovery',
    title: 'Client Relationship Recovery',
    description: 'Save an important client relationship',
    challenge: 'A key client is considering leaving due to service issues. Maya must craft a response that acknowledges problems and rebuilds trust.',
    difficulty: 'advanced',
    category: 'email'
  },
  {
    id: 'social-announcement',
    title: 'Program Launch Announcement',
    description: 'Create excitement for the new summer program',
    challenge: 'Maya wants to announce the summer program on social media in a way that generates buzz and enrollment.',
    difficulty: 'beginner',
    category: 'social'
  }
];

const STORY_STAGES = {
  'board-email': [
    {
      id: 'initial',
      title: 'The Crisis Unfolds',
      narrative: "Maya stares at her computer screen. The summer program launch is delayed by three weeks, and she needs to inform the board. Her first instinct is to quickly send: 'Hi everyone, we're running a bit behind on the summer program. It'll be ready soon!'",
      choices: [
        {
          id: 'quick-send',
          text: 'Send the quick message - it\'s honest and direct',
          impact: 'negative',
          consequence: 'The board responds with concern and multiple follow-up questions, creating more work and stress.'
        },
        {
          id: 'pause-think',
          text: 'Pause and think about what the board really needs to know',
          impact: 'positive',
          consequence: 'Maya takes time to consider the board\'s perspective and their concerns about the program.'
        }
      ]
    },
    {
      id: 'framework-learning',
      title: 'Discovering PACE',
      narrative: "Maya remembers Lyra's PACE framework from her training. She realizes this isn't just about delivering news - it's about strategic communication that maintains trust while addressing concerns.",
      lesson: "Effective communication requires structure. PACE helps you organize your thoughts and message for maximum impact.",
      tips: [
        "Purpose: What specific outcome do you want?",
        "Audience: What does the board care most about?", 
        "Connection: How can you maintain their confidence?",
        "Engagement: What actions do you want them to take?"
      ]
    },
    {
      id: 'audience-analysis',
      title: 'Understanding the Board',
      narrative: "Maya thinks about her board members: busy executives who value transparency but also need reassurance. They're not interested in excuses - they want solutions and confidence in leadership.",
      choices: [
        {
          id: 'focus-problems',
          text: 'Focus on the problems and delays',
          impact: 'negative',
          consequence: 'Creates anxiety and questions about Maya\'s leadership capability.'
        },
        {
          id: 'solution-focused',
          text: 'Lead with solutions and revised timeline',
          impact: 'positive',
          consequence: 'Demonstrates proactive leadership and maintains board confidence.'
        }
      ]
    }
  ]
};

export const TouchFriendlyMaya: React.FC<{
  scenarioId?: string;
  onComplete?: (result: any) => void;
}> = ({ scenarioId, onComplete }) => {
  const [currentScenario, setCurrentScenario] = useState<MayaScenario | null>(
    scenarioId ? MAYA_SCENARIOS.find(s => s.id === scenarioId) || null : null
  );
  const [currentStage, setCurrentStage] = useState(0);
  const [storyChoices, setStoryChoices] = useState<Record<string, string>>({});
  const [showPromptBuilder, setShowPromptBuilder] = useState(false);
  const [generatedOutputs, setGeneratedOutputs] = useState<any[]>([]);
  
  const handleScenarioSelect = (scenario: MayaScenario) => {
    setCurrentScenario(scenario);
    setCurrentStage(0);
    setStoryChoices({});
    setShowPromptBuilder(false);
    setGeneratedOutputs([]);
  };

  const handleStoryChoice = (stageId: string, choiceId: string) => {
    setStoryChoices(prev => ({ ...prev, [stageId]: choiceId }));
    
    // Auto-advance to next stage after choice
    setTimeout(() => {
      setCurrentStage(prev => prev + 1);
    }, 1500);
  };

  const handlePromptBuilderComplete = (outputs: any[]) => {
    setGeneratedOutputs(outputs);
    setShowPromptBuilder(false);
  };

  if (!currentScenario) {
    return <ScenarioSelector onSelect={handleScenarioSelect} />;
  }

  const storyStages = STORY_STAGES[currentScenario.id as keyof typeof STORY_STAGES] || [];
  const currentStageData = storyStages[currentStage];

  if (showPromptBuilder) {
    return (
      <MayaPromptWorkshop
        scenario={currentScenario}
        choices={storyChoices}
        onComplete={handlePromptBuilderComplete}
        onBack={() => setShowPromptBuilder(false)}
      />
    );
  }

  if (generatedOutputs.length > 0) {
    return (
      <AIOutputComparison
        outputs={generatedOutputs}
        title="Compare Maya's Options"
        description="Which approach do you think works better for Maya's situation?"
        onSelectionComplete={(result) => {
          if (onComplete) {
            onComplete({
              scenario: currentScenario,
              choices: storyChoices,
              selectedOutput: result.winner,
              reasons: result.reasons
            });
          }
        }}
        showPrompts={true}
        enableMetrics={true}
      />
    );
  }

  if (!currentStageData) {
    return (
      <div className="text-center p-8">
        <h3 className="text-xl font-semibold mb-4">Ready to Practice?</h3>
        <p className="text-gray-600 mb-6">
          Now let's help Maya craft the perfect message using everything you've learned.
        </p>
        <Button 
          onClick={() => setShowPromptBuilder(true)}
          className="bg-gradient-to-r from-purple-600 to-pink-600"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Build Maya's Message
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <StoryViewer
        scenario={currentScenario}
        stage={currentStageData}
        stageIndex={currentStage}
        totalStages={storyStages.length}
        onChoice={handleStoryChoice}
        onContinue={() => setCurrentStage(prev => prev + 1)}
        choices={storyChoices}
      />
    </div>
  );
};

const ScenarioSelector: React.FC<{
  onSelect: (scenario: MayaScenario) => void;
}> = ({ onSelect }) => {
  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <motion.div
          className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <Heart className="w-10 h-10 text-white" />
        </motion.div>
        <h2 className="text-2xl font-bold mb-2">Maya's Communication Challenges</h2>
        <p className="text-gray-600">Choose a scenario to help Maya improve her communication skills</p>
      </div>

      <div className="space-y-4">
        {MAYA_SCENARIOS.map((scenario, index) => (
          <motion.div
            key={scenario.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="cursor-pointer hover:shadow-md transition-all">
              <CardContent className="p-6" onClick={() => onSelect(scenario)}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{scenario.title}</h3>
                    <Badge 
                      variant={scenario.difficulty === 'beginner' ? 'secondary' : 
                              scenario.difficulty === 'intermediate' ? 'default' : 'destructive'}
                      className="text-xs"
                    >
                      {scenario.difficulty}
                    </Badge>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {scenario.category}
                  </Badge>
                </div>
                <p className="text-gray-600 text-sm mb-3">{scenario.description}</p>
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Challenge:</strong> {scenario.challenge}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MessageSquare className="w-4 h-4" />
                    Interactive Story
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const StoryViewer: React.FC<{
  scenario: MayaScenario;
  stage: StoryStage;
  stageIndex: number;
  totalStages: number;
  onChoice: (stageId: string, choiceId: string) => void;
  onContinue: () => void;
  choices: Record<string, string>;
}> = ({ scenario, stage, stageIndex, totalStages, onChoice, onContinue, choices }) => {
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const hasExistingChoice = choices[stage.id];

  const handleChoiceSelect = (choiceId: string) => {
    setSelectedChoice(choiceId);
    setTimeout(() => {
      onChoice(stage.id, choiceId);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <Badge variant="outline">{scenario.title}</Badge>
        <div className="text-sm text-gray-500">
          {stageIndex + 1} of {totalStages}
        </div>
      </div>

      <div className="flex justify-center mb-4">
        <div className="flex gap-2">
          {Array.from({ length: totalStages }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all ${
                i === stageIndex ? 'bg-purple-500 w-6' : 
                i < stageIndex ? 'bg-green-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Stage Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            {stage.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Narrative */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-800 leading-relaxed">{stage.narrative}</p>
            </div>

            {/* Learning Content */}
            {stage.lesson && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500"
              >
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800 mb-2">Key Learning</h4>
                    <p className="text-blue-700 text-sm">{stage.lesson}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Tips */}
            {stage.tips && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-800 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Quick Tips
                </h4>
                {stage.tips.map((tip, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    {tip}
                  </motion.div>
                ))}
              </div>
            )}

            {/* Choices */}
            {stage.choices && !hasExistingChoice && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-800">What should Maya do?</h4>
                {stage.choices.map((choice, index) => (
                  <motion.button
                    key={choice.id}
                    className={`w-full p-4 rounded-lg border text-left transition-all ${
                      selectedChoice === choice.id
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => handleChoiceSelect(choice.id)}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{choice.text}</span>
                      {selectedChoice === choice.id ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          <Check className="w-5 h-5 text-blue-600" />
                        </motion.div>
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}

            {/* Show consequence if choice was made */}
            {hasExistingChoice && stage.choices && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg border-l-4 ${
                  stage.choices.find(c => c.id === hasExistingChoice)?.impact === 'positive'
                    ? 'bg-green-50 border-green-500'
                    : 'bg-orange-50 border-orange-500'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={
                    stage.choices.find(c => c.id === hasExistingChoice)?.impact === 'positive'
                      ? 'text-green-600'
                      : 'text-orange-600'
                  }>
                    {stage.choices.find(c => c.id === hasExistingChoice)?.impact === 'positive' ? 
                      <TrendingUp className="w-5 h-5" /> : 
                      <Award className="w-5 h-5" />
                    }
                  </div>
                  <div>
                    <h4 className={`font-medium mb-1 ${
                      stage.choices.find(c => c.id === hasExistingChoice)?.impact === 'positive'
                        ? 'text-green-800'
                        : 'text-orange-800'
                    }`}>
                      Result
                    </h4>
                    <p className={`text-sm ${
                      stage.choices.find(c => c.id === hasExistingChoice)?.impact === 'positive'
                        ? 'text-green-700'
                        : 'text-orange-700'
                    }`}>
                      {stage.choices.find(c => c.id === hasExistingChoice)?.consequence}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Continue Button */}
      {(hasExistingChoice || !stage.choices) && (
        <div className="flex justify-center">
          <Button onClick={onContinue}>
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

const MayaPromptWorkshop: React.FC<{
  scenario: MayaScenario;
  choices: Record<string, string>;
  onComplete: (outputs: any[]) => void;
  onBack: () => void;
}> = ({ scenario, choices, onComplete, onBack }) => {
  // This would integrate with the MobilePromptBuilder
  // For now, showing a simplified version
  
  const mockOutputs = [
    {
      id: 'basic',
      title: "Maya's First Attempt",
      content: "Hi everyone, just wanted to update you on the summer program. We're running a bit behind schedule due to some unexpected challenges, but we're working hard to get everything ready. Thanks for your patience!",
      promptUsed: "Write an email to the board about program delays",
      metrics: { clarity: 6, engagement: 4, professionalism: 7, effectiveness: 5 },
      highlights: ["Honest", "Apologetic"],
      weaknesses: ["Vague", "No specifics", "Lacks confidence"],
      audience: "Board Members",
      tone: "Apologetic",
      generatedAt: new Date()
    },
    {
      id: 'pace',
      title: "Using PACE Framework",
      content: "Dear Board Members,\n\nI'm writing to provide a strategic update on our summer program launch. While we've encountered implementation challenges that will delay our original timeline by 3 weeks, I want to share our revised plan and the significant improvements this additional time will enable.\n\nOur team has identified key opportunities to enhance program quality, and we're implementing solutions that will deliver 25% better participant outcomes. The new launch date of July 15th positions us perfectly for peak summer engagement.\n\nI'll be scheduling individual calls this week to discuss your specific questions and share the detailed roadmap. Your continued confidence in our mission drives our commitment to excellence.\n\nBest regards,\nMaya",
      promptUsed: "Using PACE framework: PURPOSE: Maintain board confidence while transparently updating on delays. AUDIENCE: Busy executives who value solutions over problems. CONNECTION: Professional confidence with transparency. ENGAGEMENT: Clear next steps and invitation for dialogue.",
      metrics: { clarity: 9, engagement: 8, professionalism: 9, effectiveness: 9 },
      highlights: ["Solution-focused", "Specific timeline", "Proactive leadership", "Clear next steps"],
      weaknesses: ["Could be more concise"],
      audience: "Board Members", 
      tone: "Professional",
      generatedAt: new Date()
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <div>
          <h3 className="text-xl font-semibold">Maya's Prompt Workshop</h3>
          <p className="text-gray-600 text-sm">{scenario.title}</p>
        </div>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-800 mb-2">Maya's Challenge</h4>
          <p className="text-blue-700 text-sm">{scenario.challenge}</p>
        </CardContent>
      </Card>

      <div className="text-center">
        <p className="text-gray-600 mb-4">
          Let's compare Maya's two approaches to this communication challenge:
        </p>
        <Button 
          onClick={() => onComplete(mockOutputs)}
          className="bg-gradient-to-r from-purple-600 to-pink-600"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Compare Maya's Options
        </Button>
      </div>
    </div>
  );
};

export default TouchFriendlyMaya;